/**
 * Dates inside badges and facts. Those are computed on the server and translated on the web with
 * plain interpolation, so a raw ISO date would read "2027-03-12" in both languages. One catalog
 * template per month keeps "Mar 12" in English and "12 mar" in Spanish without a date library.
 */
import { msg, type Msg, type Quantity } from "../../../contract";

const DATE_WITH_YEAR = [
  "Jan {{day}}, {{year}}", "Feb {{day}}, {{year}}", "Mar {{day}}, {{year}}", "Apr {{day}}, {{year}}", "May {{day}}, {{year}}", "Jun {{day}}, {{year}}",
  "Jul {{day}}, {{year}}", "Aug {{day}}, {{year}}", "Sep {{day}}, {{year}}", "Oct {{day}}, {{year}}", "Nov {{day}}, {{year}}", "Dec {{day}}, {{year}}",
] as const;

const PREGNANT_DUE = [
  "Pregnant · due Jan {{day}}", "Pregnant · due Feb {{day}}", "Pregnant · due Mar {{day}}", "Pregnant · due Apr {{day}}", "Pregnant · due May {{day}}", "Pregnant · due Jun {{day}}",
  "Pregnant · due Jul {{day}}", "Pregnant · due Aug {{day}}", "Pregnant · due Sep {{day}}", "Pregnant · due Oct {{day}}", "Pregnant · due Nov {{day}}", "Pregnant · due Dec {{day}}",
] as const;

/** Every dynamic template, for the localization check. */
export const DATE_MESSAGES: string[] = [...DATE_WITH_YEAR, ...PREGNANT_DUE];

function parts(iso: string): { month: number; day: number; year: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
}

/** "Mar 12, 2027" / "12 mar 2027". */
export function dateMsg(iso: string): Msg {
  const p = parts(iso);
  if (!p) return msg(iso);
  return msg(DATE_WITH_YEAR[p.month], { day: p.day, year: p.year });
}

/** "Pregnant · due Mar 12" / "Preñada · pare 12 mar". */
export function pregnantDueMsg(iso: string): Msg {
  const p = parts(iso);
  if (!p) return msg("Pregnant");
  return msg(PREGNANT_DUE[p.month], { day: p.day });
}

/** "1 day" / "{{count}} days" without a plural engine on the server. */
export function daysMsg(count: number): Msg {
  return count === 1 ? msg("1 day") : msg("{{count}} days", { count });
}

/** Fetal age in days from a recorded quantity, keeping the unit the examiner used. */
export function ageInDays(q: Quantity | null | undefined): number | undefined {
  if (!q || typeof q.value !== "number" || !Number.isFinite(q.value) || q.value <= 0) return undefined;
  switch (q.unit) {
    case "d": return Math.round(q.value);
    case "wk": return Math.round(q.value * 7);
    case "mo": return Math.round(q.value * 30.4);
    default: return undefined;
  }
}

/** The calendar date of an event (YYYY-MM-DD), placing partial dates mid-period. Null when unknown. */
export function eventDay(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  if (/^\d{4}-\d{2}$/.test(value)) return `${value}-15`;
  if (/^\d{4}$/.test(value)) return `${value}-07-01`;
  return null;
}
